/**
 * LRU of recently-viewed fiche IDs, persisted in localStorage (per-browser,
 * not synced across devices — that's a deliberate trade-off: writing to
 * Firestore on every navigation would be noisy and costs credits). Keeps
 * the latest `MAX` entries, newest first.
 */

import { useEffect, useState } from "react";

const KEY = "pluview.recentFicheIds.v1";
const MAX = 10;

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function write(ids: string[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
    // Notify listeners in the same tab — the native "storage" event only
    // fires in other tabs, so we emit a custom event for ourselves.
    window.dispatchEvent(new CustomEvent("pluview:recent-fiches-changed"));
  } catch {
    // Storage is full / disabled — silently drop.
  }
}

// Push a ficheId to the front of the LRU. Call once when a Fiche page mounts.
export function recordRecentFiche(ficheId: string): void {
  if (!ficheId) return;
  const current = read();
  const next = [ficheId, ...current.filter((id) => id !== ficheId)];
  write(next);
}

// React hook — returns the current LRU, re-renders when it changes in
// this tab or in another (via the "storage" event).
export function useRecentFicheIds(): string[] {
  const [ids, setIds] = useState<string[]>(() => read());
  useEffect(() => {
    const handler = () => setIds(read());
    window.addEventListener("storage", handler);
    window.addEventListener("pluview:recent-fiches-changed", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("pluview:recent-fiches-changed", handler);
    };
  }, []);
  return ids;
}
